export type VoiceSignal = {
  type: "announce" | "offer" | "answer" | "ice" | "leave";
  from: string;
  to?: string;
  sdp?: any;
  candidate?: any;
};

export type VoicePeer = {
  id: string;
  pc: RTCPeerConnection;
  audioEl: HTMLAudioElement;
  stream?: MediaStream;
  analyser?: AnalyserNode;
  level?: number;
};

export class VoiceChatManager {
  private readonly sendSignal: (payload: unknown) => void;
  private readonly clientId: string;
  private localStream: MediaStream | null = null;
  private peers = new Map<string, VoicePeer>();
  private muted = false;
  private onPeerCountChange: ((count: number) => void) | null = null;
  private analyserTimer: number | null = null;

  static defaultIce(): RTCIceServer[] {
    return [
      { urls: "stun:stun.l.google.com:19302" },
    ];
  }

  constructor(sendSignal: (payload: unknown) => void, onPeerCountChange?: (count: number) => void) {
    this.sendSignal = sendSignal;
    this.clientId = (crypto && typeof (crypto as any).randomUUID === "function") ? (crypto as any).randomUUID() : `${Date.now()}-${Math.floor(Math.random() * 100000)}`;
    this.onPeerCountChange = onPeerCountChange ?? null;
  }

  id(): string { return this.clientId; }

  async start(): Promise<void> {
    if (this.localStream) return;
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.setMuted(this.muted); // apply muted state
      // Announce presence to other clients
      this.sendSignal({ type: "announce", from: this.clientId });
      // monitor levels
      this.startAnalyser();
    } catch (err) {
      console.error("VoiceChat: failed to getUserMedia", err);
      throw err;
    }
  }

  async stop(): Promise<void> {
    try {
      // notify others
      this.sendSignal({ type: "leave", from: this.clientId });
      // close peers
      for (const [, peer] of this.peers) {
        try { peer.pc.close(); } catch {}
        try { peer.audioEl.srcObject = null; } catch {}
      }
      this.peers.clear();
      // stop local tracks
      if (this.localStream) {
        for (const track of this.localStream.getTracks()) track.stop();
        this.localStream = null;
      }
      this.stopAnalyser();
      this.notifyPeerCount();
    } catch (err) {
      console.error("VoiceChat stop error", err);
    }
  }

  isStarted(): boolean { return !!this.localStream; }
  isMuted(): boolean { return this.muted; }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.localStream) {
      for (const t of this.localStream.getAudioTracks()) t.enabled = !muted;
    }
  }

  private createPeerConnection(peerId: string): VoicePeer {
    const pc = new RTCPeerConnection({ iceServers: VoiceChatManager.defaultIce() });
    const audioEl = document.createElement("audio");
    audioEl.autoplay = true; audioEl.controls = false; audioEl.preload = "auto";
    const peer: VoicePeer = { id: peerId, pc, audioEl };

    pc.onicecandidate = (ev) => {
      if (ev.candidate) this.sendSignal({ type: "ice", from: this.clientId, to: peerId, candidate: ev.candidate });
    };

    pc.ontrack = (ev) => {
      try {
        const ms = ev.streams && ev.streams[0] ? ev.streams[0] : new MediaStream(ev.track ? [ev.track] : []);
        peer.audioEl.srcObject = ms as MediaStream;
        peer.stream = ms as MediaStream;
        this.setupAnalyserForPeer(peer);
      } catch (err) { console.error("ontrack", err); }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "failed" || pc.connectionState === "closed") {
        this.removePeer(peerId);
      }
    };

    // add local tracks
    if (this.localStream) for (const track of this.localStream.getTracks()) pc.addTrack(track, this.localStream);

    this.peers.set(peerId, peer);
    this.notifyPeerCount();
    return peer;
  }

  private removePeer(peerId: string) {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    try { peer.pc.close(); } catch {}
    try { peer.audioEl.srcObject = null; } catch {}
    this.peers.delete(peerId);
    this.notifyPeerCount();
  }

  private notifyPeerCount() { if (this.onPeerCountChange) this.onPeerCountChange(this.peers.size); }

  private async initiateOffer(peerId: string) {
    const peer = this.peers.get(peerId) ?? this.createPeerConnection(peerId);
    try {
      const offer = await peer.pc.createOffer();
      await peer.pc.setLocalDescription(offer);
      this.sendSignal({ type: "offer", from: this.clientId, to: peerId, sdp: offer });
    } catch (err) { console.error("initiateOffer", err); }
  }

  private async handleOffer(from: string, sdp: any) {
    const peer = this.peers.get(from) ?? this.createPeerConnection(from);
    try {
      await peer.pc.setRemoteDescription(sdp);
      const answer = await peer.pc.createAnswer();
      await peer.pc.setLocalDescription(answer);
      this.sendSignal({ type: "answer", from: this.clientId, to: from, sdp: answer });
    } catch (err) { console.error("handleOffer", err); }
  }

  private async handleAnswer(from: string, sdp: any) {
    const peer = this.peers.get(from);
    if (!peer) return;
    try { await peer.pc.setRemoteDescription(sdp); } catch (err) { console.error("handleAnswer", err); }
  }

  private async handleIce(from: string, candidate: any) {
    const peer = this.peers.get(from);
    if (!peer) return;
    try { await peer.pc.addIceCandidate(candidate); } catch (err) { console.error("addIceCandidate", err); }
  }

  // incoming signal from room
  async handleSignal(payload: any) {
    if (!payload || typeof payload !== "object") return;
    const { type, from, to } = payload as VoiceSignal;
    if (!from || from === this.clientId) return;
    if (to && to !== this.clientId) return; // not for us

    switch (type) {
      case "announce":
        // create peer and decide who offers: deterministic ordering
        if (!this.peers.has(from)) this.createPeerConnection(from);
        // deterministic: highest id offers
        if (this.clientId > from) await this.initiateOffer(from);
        break;
      case "offer":
        await this.handleOffer(from, payload.sdp);
        break;
      case "answer":
        await this.handleAnswer(from, payload.sdp);
        break;
      case "ice":
        await this.handleIce(from, payload.candidate);
        break;
      case "leave":
        this.removePeer(from);
        break;
      default:
        break;
    }
  }

  private setupAnalyserForPeer(peer: VoicePeer) {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const analyser = ctx.createAnalyser(); analyser.fftSize = 256;
      const source = ctx.createMediaStreamSource(peer.stream as MediaStream);
      source.connect(analyser);
      peer.analyser = analyser;
      // start sampling loop
      if (!this.analyserTimer) this.analyserTimer = window.setInterval(() => this.sampleLevels(), 200);
    } catch (err) { /* ignore analyser failures */ }
  }

  private sampleLevels() {
    for (const [, peer] of this.peers) {
      if (!peer.analyser) { peer.level = 0; continue; }
      const analyser = peer.analyser;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        sum += buf[i]!;
      }
      const avg = sum / buf.length;
      peer.level = avg / 256;
    }
  }

  private startAnalyser() { if (this.localStream) { /* nothing for local for now */ } }
  private stopAnalyser() { if (this.analyserTimer) { window.clearInterval(this.analyserTimer); this.analyserTimer = null; } }

  getPeers(): { id: string; level: number }[] { return Array.from(this.peers.values()).map((p) => ({ id: p.id, level: p.level ?? 0 })); }
}
